import {z, ZodSchema} from 'zod';
import type {
  AuthorizationResourceType,
  AuthorizationAction,
  AuthorizationResource,
} from '@packages/domains/identity-and-access/role-based-access/data-types';

export interface ToolPrettyError {
  match: string;
  message: string;
  /** AI-facing recovery directive injected into the tool response when this error fires. */
  aiInstruction?: string;
}

export interface ToolPrettyErrors {
  fallback: string;
  patterns?: ToolPrettyError[];
}

interface ToolPermissionEntry<R extends AuthorizationResourceType, A> {
  resource: R;
  action: AuthorizationAction<R>;
  /**
   * Optional function that resolves the specific resources to check permissions against.
   * Receives the validated tool arguments and returns an array of resources (type + ID).
   * When omitted, permission is checked without a specific resource ID.
   * Use ANY_RESOURCE_ID as the id when no specific resource is targeted.
   *
   * Returning `[]` is treated as fail-closed (the tool call is denied) — return
   * `[{type, id: ANY_RESOURCE_ID}]` instead if you want a site-level fallback.
   */
  resolveResources?(args: A): AuthorizationResource<R>[];
}

/**
 * Discriminated union of all valid resource+action pairs.
 * Forces TypeScript to correlate `action` to the specific `resource` — prevents
 * e.g. `{resource: 'SitePage', action: 'read'}` from passing when `read` is not
 * a valid SitePage action.
 *
 * Generic over `A` (the tool's argument type) so that `resolveResources` is
 * typed against the actual arguments rather than `unknown`.
 */
export type ToolPermission<A = unknown> = {
  [R in AuthorizationResourceType]: ToolPermissionEntry<R, A>;
}[AuthorizationResourceType];

export interface ToolRegistryManifest<
  T extends string,
  TSchema extends ZodSchema,
> {
  id: T;
  prettyName: string; // Human readable name for displaying the tool call
  family?: string; // Tool family for compound grouping (e.g. 'cms', 'pages', 'site')
  prettyStatus?: {
    executed: string;
    executing: string;
    failed: string;
    partial?: string;
  };
  prettyErrors?: ToolPrettyErrors;
  llmTool: {
    name: T;
    description: string;
    inputSchema: TSchema;
  };
  /** Optional execution schema; the model still sees llmTool.inputSchema. */
  parseSchema?: z.ZodType<z.infer<TSchema>, z.ZodTypeDef, unknown>;
  type: 'designer' | 'server'; // Where the tool is executed
  /** Optionally include or exclude the tool using a Statsig feature gate. */
  featureGate?: string | {flag: string; value: boolean};
  /**
   * OAuth scopes the caller's grant must include for this tool to be exposed
   * to the LLM. Used by the agent's tool-catalog filter (AIO-395 / AIO-210
   * step 2) when the request arrived via OAuth-bearer auth (the slack agent
   * + public MCP path). Undeclared means "no scope requirement known" — the
   * filter currently treats that as fail-open so the slack agent's render
   * surface (~115 undeclared tools as of 2026-06) keeps working while the
   * render-tool scope backfill ticket lands. JWT-auth callers (AIA) skip the
   * scope filter entirely.
   */
  requiredScopes?: string[];
  permission?: ToolPermission<z.infer<TSchema>>[]; // GRACE/Oso permissions required to use this tool
  confirmBeforeExecuting: boolean; // Whether to confirm with the user before executing the tool
  validate: (args: unknown) => z.infer<TSchema>; // Validate the arguments passed to the tool
  /** Whether this tool call can run concurrently with other safe tool calls. Defaults to false. */
  isConcurrencySafe?(args: z.infer<TSchema>): boolean;
  /** Whether this tool call only reads state and does not mutate anything. Defaults to false. */
  isReadOnly?(args: z.infer<TSchema>): boolean;
  timeout?: number; // Optional timeout in milliseconds for tool execution
  compaction?: {
    prompt: string;
    staleAfterTurns?: number;
    /** When true, a newer call to this tool makes all earlier results immediately stale.
     *  Only safe for tools that return a full snapshot (e.g. read-page, site-wide extractions).
     *  Not safe for tools where arguments select different data (e.g. get-html-from-selected-element). */
    supersededOnRecall?: boolean;
  };
}

export type AnyToolRegistryManifest = ToolRegistryManifest<string, ZodSchema>;

export function defineToolRegistryManifest<
  T extends string,
  TSchema extends ZodSchema,
>(
  manifest: Omit<ToolRegistryManifest<T, TSchema>, 'validate'> & {
    validate?: (args: unknown) => z.infer<TSchema>;
  }
): ToolRegistryManifest<T, TSchema> {
  return {
    ...manifest,
    validate:
      manifest.validate ??
      ((args: unknown): z.infer<TSchema> =>
        (manifest.parseSchema ?? manifest.llmTool.inputSchema).parse(args)),
  };
}

export type ToolArgumentType<
  T extends ToolRegistryManifest<string, ZodSchema>,
> =
  T extends ToolRegistryManifest<string, infer TSchema>
    ? z.infer<TSchema>
    : never;
